import { useState, useEffect } from 'react'
import { SelectYesNo } from '../../components'
import { Spinner } from '@fluentui/react'
import TrusteeForm from './trustee-form'
import { IStreetAddress } from '@oneethos/shared'
import { SubformProps, Validation } from '.'

const PropertyOwnership = ({
  formData,
  update,
  saving,
  save
}: SubformProps) => {
  const [showTrusteeForm, setShowTrusteeForm] = useState(false)
  const [validation, setValidation] = useState<Record<string, string>>({})

  useEffect(() => {
    if (formData.propertyHeldInTrust === 'no' && formData.trustees.length > 0) {
      update('trustees', [])
    }
  }, [formData.propertyHeldInTrust, update])

  const isPropertyHeldInTrust = formData.propertyHeldInTrust === 'yes' ? true : false
  const hasCoborrower = formData.isJointApp
  const borrower = `${formData.firstName} ${formData.lastName}`
  const coBorrower = `${formData.coFirstName} ${formData.coLastName}`
  const hasFourTrustees = formData.trustees.length >= 4

  const additionalTrustees = formData.trustees?.filter(t =>
    t.email !== formData.email && t.email !== formData.coEmail)

  const handleTrusteeOnChange = (
    isChecked: boolean,
    name: string,
    email: string,
    address: IStreetAddress,
    phone: string
  ) => {
    if (isChecked) {
      if (!formData.trustees.some(t => t.name === name)) {
        update('trustees', [...formData.trustees, {
          name,
          email,
          phone,
          address,
        }])
      }
    } else {
      update('trustees', formData.trustees.filter(t => t.name !== name))
    }
  }

  const handleShowTrusteeForm = () => {
    setShowTrusteeForm(true)
  }

  const handleTrusteeFormClose = () => {
    setShowTrusteeForm(false)
  }

  const validate = () => {
    const results: Record<string, string> = {}
    if (!['yes', 'no'].includes(formData.propertyHeldInTrust)) {
      results['propertyHeldInTrust'] = 'Please indicate whether or not the property is held in a trust.'
    }
    return results
  }

  const onValidation = () => {
    const v = validate()
    setValidation(v)
    if (Object.keys(v).length === 0) {
      save()
    }
  }

  return (
    <div>
      <h2>Property Ownership</h2>
      <div className="form-group">
        <label>Is the property where the equipment will be located held in a trust?</label>
        <SelectYesNo
          value={formData.propertyHeldInTrust || ''}
          onChange={(val) => {
            update('propertyHeldInTrust', val)
            setValidation({})
          }}
        />
      </div>
      {isPropertyHeldInTrust ?
        <>
          <label>Select any borrowers who are trustees and add any additional trustees not in the loan.</label>
          <div className="input-group mb-3">
            <div className="input-group-text">
              <input
                className="form-check-input mt-0"
                type="checkbox"
                checked={formData.trustees.some(t => t.name === borrower)}
                onChange={(e) => {
                  handleTrusteeOnChange(
                    e.target.checked,
                    borrower,
                    formData.email,
                    formData.userAddress,
                    formData.phone
                  )
                }}
              />
            </div>
            <input type="text" className="form-control" value={borrower} readOnly />
          </div>
          {hasCoborrower &&
            <div className="input-group mb-3">
              <div className="input-group-text">
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  checked={formData.trustees.some(t => t.name === coBorrower)}
                  onChange={(e) => {
                    handleTrusteeOnChange(
                      e.target.checked,
                      coBorrower,
                      formData.coEmail,
                      formData.coBorrowerAddress,
                      formData.coPhone
                    )
                  }}
                />
              </div>
              <input type="text" className="form-control" value={coBorrower} readOnly />
            </div>
          }
          {formData.trustees.length > 0 && (
            <div className="form-group">
              {additionalTrustees.map((trustee, index) => (
                <TrusteeForm
                  key={index}
                  update={update}
                  formData={formData}
                  onClose={() => {
                    update('trustees', formData.trustees.filter((_, i) => i !== index))
                  }}
                  initialData={trustee}
                  isEditing={true}
                />
              ))}
            </div>
          )}
          {showTrusteeForm && (
            <TrusteeForm
              update={update}
              formData={formData}
              onClose={handleTrusteeFormClose}
            />
          )}
        </>
        : null}
      {!hasFourTrustees && !showTrusteeForm && isPropertyHeldInTrust && (
        <div className="form-group">
          <button
            className='btn btn-secondary'
            type='button'
            onClick={handleShowTrusteeForm}
          >
            Add a new trustee
          </button>
        </div>
      )}
      {!showTrusteeForm &&
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving || (isPropertyHeldInTrust && formData.trustees.length === 0)}
          onClick={onValidation}
        >
          {saving ? <Spinner /> : 'Continue'}
        </button>}
      {validation && Object.keys(validation).length > 0 ? (
        <Validation validation={validation} />
      ) : null}
    </div>
  )
}

export default PropertyOwnership
